<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import { ArrowLeft, ArrowRight } from '@lucide/vue';
import { EvolfinApiError } from '@/api/client';
import { listaTipiDocumentoCliente } from '@/api/file';
import { useAuthStore } from '@/stores/auth';
import { settingsKeys } from '@/features/impostazioni/query-keys';
import { useAvvisi } from '@/composables/useAvvisi';
import { useInvalidation } from '@/composables/useInvalidation';
import AvvisoErrore from '@/components/ui/AvvisoErrore.vue';
import BarraPassi from '@/components/ui/BarraPassi.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import { useClientForm, type FieldErrors } from '../composables/useClientForm';
import { creaCliente } from '../api';
import SezioneIdentita from '../components/form/SezioneIdentita.vue';
import SezioneSedeLegale from '../components/form/SezioneSedeLegale.vue';
import SezioneContatti from '../components/form/SezioneContatti.vue';
import SezioneFatturazione from '../components/form/SezioneFatturazione.vue';
import SezioneProvenienza from '../components/form/SezioneProvenienza.vue';
import PassoDocumentiRichiesti from '../components/PassoDocumentiRichiesti.vue';

definePage({ meta: { titolo: 'Nuovo cliente', privilegi: ['can_create_clienti'] } });

const router = useRouter();
const auth = useAuthStore();
const avvisi = useAvvisi();
// L'elenco da cui si è arrivati non sa ancora della riga nuova.
const invalidate = useInvalidation('clienti');

/**
 * Ogni passo dei dati convalida i propri campi prima di lasciarlo: quelli senza
 * campi obbligatori si superano vuoti, ma ciò che si è scritto deve essere
 * valido. Il cliente nasce alla fine dei dati, con una chiamata sola; i documenti
 * vengono dopo, perché un file si aggancia a un cliente che esiste già.
 */
const PASSI_DATI = [
  {
    titolo: 'Identità',
    campi: [
      'ragioneSociale',
      'nomeCommerciale',
      'tipologiaId',
      'codiceFiscale',
      'partitaIva',
      'codiceAteco',
    ],
  },
  { titolo: 'Sede legale', campi: ['sedeLegale', 'sedeLegaleTraOperative'] },
  { titolo: 'Recapiti', campi: ['email', 'telefono', 'pec', 'codiceSdi'] },
  { titolo: 'Provenienza', campi: ['stato', 'canaleAcquisizione', 'partnerPersonaSegnalatoreId'] },
] as const;
const passoCorrente = ref(0);
const ultimoPassoDati = computed(() => passoCorrente.value === PASSI_DATI.length - 1);

const { data: tipiDocumento } = useQuery({
  queryKey: settingsKeys.activeClientDocumentTypes(),
  queryFn: listaTipiDocumentoCliente,
  staleTime: 5 * 60 * 1000,
});
const tipiRichiesti = computed(
  () => tipiDocumento.value?.filter((t) => t.richiestoPerProgetto) ?? [],
);
// Senza tipi richiesti, o senza il permesso di caricarli, il passo non avrebbe niente da offrire.
const conPassoDocumenti = computed(
  () => tipiRichiesti.value.length > 0 && auth.ha('can_write_clienti'),
);
const titoliPassi = computed(() => [
  ...PASSI_DATI.map((p) => p.titolo),
  ...(conPassoDocumenti.value ? ['Documenti'] : []),
]);
/** Valorizzato alla creazione: da lì in poi resta solo il passo documenti. */
const clienteCreatoId = ref<number | null>(null);

const { form, fieldErrors, validateFields, validateCreate } = useClientForm();
const erroreGenerale = ref<string | null>(null);
const salvataggio = ref(false);

function avanti(): void {
  erroreGenerale.value = null;
  if (!validateFields(PASSI_DATI[passoCorrente.value]!.campi)) return;
  if (!ultimoPassoDati.value) passoCorrente.value += 1;
}

function indietro(): void {
  fieldErrors.value = {};
  erroreGenerale.value = null;
  passoCorrente.value -= 1;
}

function vaiAlPasso(indice: number): void {
  if (indice > passoCorrente.value) return;
  fieldErrors.value = {};
  passoCorrente.value = indice;
}

/** Riporta sul primo passo che ha un campo in errore, così l'errore si vede. */
function mostraPrimoErrore(errori: FieldErrors): void {
  const campi = Object.keys(errori).map((k) => k.split('.')[0]!);
  const indice = PASSI_DATI.findIndex((p) => p.campi.some((c) => campi.includes(c)));
  if (indice >= 0) passoCorrente.value = indice;
}

async function crea(): Promise<void> {
  erroreGenerale.value = null;
  const dto = validateCreate();
  if (!dto) {
    mostraPrimoErrore(fieldErrors.value);
    return;
  }

  salvataggio.value = true;
  try {
    const creato = await creaCliente(dto);
    void invalidate();
    avvisi.successo('Cliente creato');
    if (conPassoDocumenti.value) {
      clienteCreatoId.value = creato.id;
      passoCorrente.value = PASSI_DATI.length;
    } else {
      await router.push(`/clienti/${creato.id}`);
    }
  } catch (errore) {
    if (errore instanceof EvolfinApiError && errore.dettagli.code === 'CODICE_FISCALE_GIA_USATO') {
      fieldErrors.value = { codiceFiscale: ['Esiste già un cliente con questo codice fiscale'] };
    } else if (errore instanceof EvolfinApiError) {
      fieldErrors.value = errore.erroriCampo;
      erroreGenerale.value = errore.message;
    } else {
      erroreGenerale.value = 'Errore imprevisto. Riprova.';
    }
    mostraPrimoErrore(fieldErrors.value);
  } finally {
    salvataggio.value = false;
  }
}
</script>

<template>
  <div class="mx-auto flex w-full max-w-3xl flex-col gap-6">
    <div>
      <button
        type="button"
        class="flex w-fit items-center gap-1.5 text-[13px] text-ink-secondary transition-colors hover:text-ink"
        @click="router.push('/clienti')"
      >
        <ArrowLeft :size="15" :stroke-width="1.9" /> Clienti
      </button>
      <h1 class="mt-3 font-display text-[28px] leading-tight font-semibold">Nuovo cliente</h1>
    </div>

    <BarraPassi
      :passi="titoliPassi"
      :corrente="passoCorrente"
      :bloccata="clienteCreatoId != null"
      @vai="vaiAlPasso"
    />

    <PassoDocumentiRichiesti
      v-if="clienteCreatoId != null"
      :cliente-id="clienteCreatoId"
      :tipi-richiesti="tipiRichiesti"
      @fine="router.push(`/clienti/${clienteCreatoId}`)"
    />

    <form
      v-else
      class="flex flex-col gap-5"
      novalidate
      @submit.prevent="ultimoPassoDati ? crea() : avanti()"
    >
      <AvvisoErrore v-if="erroreGenerale" :messaggio="erroreGenerale" />

      <SezioneIdentita v-if="passoCorrente === 0" v-model="form" :errori="fieldErrors" />
      <SezioneSedeLegale
        v-else-if="passoCorrente === 1"
        v-model="form"
        :errori="fieldErrors"
        con-copia-tra-operative
      />
      <template v-else-if="passoCorrente === 2">
        <SezioneContatti v-model="form" :errori="fieldErrors" />
        <SezioneFatturazione v-model="form" :errori="fieldErrors" />
      </template>
      <SezioneProvenienza v-else v-model="form" :errori="fieldErrors" />

      <div class="flex items-center justify-between gap-3">
        <PulsanteAzione v-if="passoCorrente > 0" variante="silenzioso" @click="indietro">
          Indietro
        </PulsanteAzione>
        <PulsanteAzione v-else variante="silenzioso" @click="router.push('/clienti')">
          Annulla
        </PulsanteAzione>

        <PulsanteAzione v-if="!ultimoPassoDati" tipo="submit">
          Continua
          <template #icona><ArrowRight :size="15" :stroke-width="2" /></template>
        </PulsanteAzione>
        <PulsanteAzione v-else tipo="submit" :caricamento="salvataggio"
          >Crea cliente</PulsanteAzione
        >
      </div>
    </form>
  </div>
</template>
